import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import {
  TrendingUp,
  TrendingDown,
  BarChart3,
  Clock,
  MessageSquare,
  ArrowRight,
  Activity,
  Gauge,
} from 'lucide-react';
import { useMarketStore } from '../store';
import { StockLogo } from '../components/common/StockLogo';
import { InfoTooltip, TOOLTIPS } from '../components/common/InfoTooltip';
import { runMarketScanner } from '../utils/api';
import { formatRelativeTime, formatSignal } from '../utils/format';
import type { MarketScanner, StockSignal } from '../types';

// Compact stock card for side-by-side view
function CompactStockCard({
  stock,
  rank,
  type,
  onClick,
}: {
  stock: StockSignal;
  rank: number;
  type: 'buy' | 'sell';
  onClick: () => void;
}) {
  const isBuy = type === 'buy';

  return (
    <div
      onClick={onClick}
      className={clsx(
        'group flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all',
        'hover:border-[var(--color-accent)]/50 hover:bg-[var(--color-bg-secondary)]',
        'bg-[var(--color-bg-primary)] border-[var(--color-border)]'
      )}
    >
      {/* Rank */}
      <span className="text-xs font-medium text-[var(--color-text-muted)] w-5">
        #{rank}
      </span>

      {/* Logo */}
      <StockLogo ticker={stock.ticker} name={stock.name} website={stock.website} size="md" />

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-bold text-[var(--color-text-primary)]">
            {stock.ticker}
          </span>
          <span className={clsx(
            'px-1.5 py-0.5 rounded text-[10px] font-semibold',
            isBuy
              ? 'bg-[var(--sentiment-bullish)]/10 text-[var(--sentiment-bullish)]'
              : 'bg-[var(--sentiment-bearish)]/10 text-[var(--sentiment-bearish)]'
          )}>
            {formatSignal(stock.signal)}
          </span>
        </div>
        <p className="text-xs text-[var(--color-text-muted)] truncate">
          {stock.name}
        </p>
      </div>

      {/* Disconnect Score (0-100 scale, 50 = neutral) */}
      <div className="text-right">
        <p className={clsx(
          'text-lg font-bold font-mono',
          isBuy ? 'text-[var(--sentiment-bullish)]' : 'text-[var(--sentiment-bearish)]'
        )}>
          {stock.disconnect_score.toFixed(0)}
        </p>
        <p className="text-[10px] text-[var(--color-text-muted)]">disconnect</p>
      </div>

      {/* Arrow */}
      <ArrowRight className="w-4 h-4 text-[var(--color-text-muted)] group-hover:text-[var(--color-accent)] transition-colors" />
    </div>
  );
}

// Metric card component
function MetricCard({
  label,
  value,
  subValue,
  icon: Icon,
  trend,
  color,
  tooltip,
}: {
  label: string;
  value: string | number;
  subValue?: string;
  icon: typeof BarChart3;
  trend?: 'up' | 'down' | 'neutral';
  color?: 'green' | 'red' | 'blue' | 'purple';
  tooltip?: string;
}) {
  const colorClasses = {
    green: 'text-[var(--sentiment-bullish)]',
    red: 'text-[var(--sentiment-bearish)]',
    blue: 'text-blue-500',
    purple: 'text-purple-500',
  };

  return (
    <div className="bg-[var(--color-bg-primary)] rounded-xl border border-[var(--color-border)] p-4 relative">
      {tooltip && (
        <div className="absolute top-2 right-2">
          <InfoTooltip content={tooltip} position="bottom" />
        </div>
      )}
      <div className="flex items-center gap-3">
        <div className={clsx(
          'w-10 h-10 rounded-lg flex items-center justify-center',
          color === 'green' && 'bg-[var(--sentiment-bullish)]/10',
          color === 'red' && 'bg-[var(--sentiment-bearish)]/10',
          color === 'blue' && 'bg-blue-500/10',
          color === 'purple' && 'bg-purple-500/10',
          !color && 'bg-[var(--color-accent)]/10'
        )}>
          <Icon className={clsx('w-5 h-5', color ? colorClasses[color] : 'text-[var(--color-accent)]')} />
        </div>
        <div className="flex-1">
          <p className="text-xs text-[var(--color-text-muted)]">{label}</p>
          <div className="flex items-baseline gap-2">
            <p className={clsx(
              'text-xl font-bold',
              trend === 'up' && 'text-[var(--sentiment-bullish)]',
              trend === 'down' && 'text-[var(--sentiment-bearish)]',
              !trend && 'text-[var(--color-text-primary)]'
            )}>
              {value}
            </p>
            {subValue && (
              <span className="text-xs text-[var(--color-text-muted)]">{subValue}</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Sentiment gauge component (0-100 scale)
// Green: >= 60 (bullish), Yellow: 40-60 (neutral), Red: <= 40 (bearish)
function SentimentGauge({ value, label }: { value: number; label: string }) {
  // Color based on score bands - same for text and bar
  const getBarColor = () => {
    if (value >= 60) return 'bg-emerald-500';
    if (value <= 40) return 'bg-red-500';
    return 'bg-amber-500';
  };

  const getTextColor = () => {
    if (value >= 60) return 'text-emerald-500';
    if (value <= 40) return 'text-red-500';
    return 'text-amber-500';
  };

  return (
    <div className="bg-[var(--color-bg-primary)] rounded-xl border border-[var(--color-border)] p-4">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs text-[var(--color-text-muted)]">{label}</p>
        <p className={clsx('text-lg font-bold font-mono', getTextColor())}>
          {value.toFixed(0)}
        </p>
      </div>
      <div className="h-2 bg-[var(--color-bg-tertiary)] rounded-full overflow-hidden">
        <div
          className={clsx('h-full rounded-full transition-all', getBarColor())}
          style={{ width: `${Math.min(100, Math.max(0, value))}%` }}
        />
      </div>
      <div className="flex justify-between mt-1 text-[10px] text-[var(--color-text-muted)]">
        <span>0</span>
        <span>50</span>
        <span>100</span>
      </div>
    </div>
  );
}

export function Dashboard() {
  const navigate = useNavigate();
  const { scannerData, setScannerData, lastScanTime, setLastScanTime } = useMarketStore();
  const [loading, setLoading] = useState(!scannerData);

  const fetchData = async () => {
    try {
      setLoading(true);
      const data: MarketScanner = await runMarketScanner();
      setScannerData(data);
      setLastScanTime(data.scan_time);
    } catch (error) {
      console.error('Failed to fetch scanner data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!scannerData) {
      fetchData();
    }
  }, []);

  const buyOpportunities = scannerData?.buy_opportunities || [];
  const sellSignals = scannerData?.sell_signals || [];
  const allStocks = [...buyOpportunities, ...sellSignals];

  // Calculate metrics
  const totalPostsAnalyzed = allStocks.reduce((sum, stock) => sum + (stock.posts_analyzed || 0), 0);

  // Average market sentiment across all analyzed stocks
  const avgSentiment = allStocks.length > 0
    ? allStocks.reduce((sum, stock) => sum + (stock.sentiment_score || 0), 0) / allStocks.length
    : 0;

  // Average disconnect score
  const avgDisconnect = allStocks.length > 0
    ? allStocks.reduce((sum, stock) => sum + Math.abs(stock.disconnect_score || 0), 0) / allStocks.length
    : 0;

  // Sentiment distribution (0-100 scale: 50=neutral, >60=bullish, <40=bearish)
  const bullishCount = allStocks.filter(s => s.sentiment_score > 60).length;
  const bearishCount = allStocks.filter(s => s.sentiment_score < 40).length;
  const neutralCount = allStocks.length - bullishCount - bearishCount;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col gap-3">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-[var(--color-text-primary)]">
              Market Dashboard
            </h1>
            <p className="text-sm text-[var(--color-text-muted)] mt-1">
              Find contrarian opportunities: low sentiment + strong fundamentals {scannerData?.stocks_analyzed ? `across ${scannerData.stocks_analyzed} stocks` : ''}
            </p>
          </div>

          {lastScanTime && (
            <div className="flex items-center gap-2 text-xs text-[var(--color-text-muted)]">
              <Clock className="w-3.5 h-3.5" />
              <span>Updated {formatRelativeTime(lastScanTime)}</span>
            </div>
          )}
        </div>
        <p className="text-xs text-[var(--color-accent)]">
          <strong>Research Tool:</strong> SentimentEdge provides sentiment analysis for research purposes. We show you what people are saying. You make your own decisions.
        </p>
      </div>

      {/* Top Metrics Grid */}
      {!loading && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {/* Market Sentiment Gauge - spans 2 cols on mobile */}
          <div className="col-span-2">
            <SentimentGauge
              value={avgSentiment}
              label="Average Market Sentiment"
            />
          </div>

          {/* Buy vs Sell */}
          <MetricCard
            label="Buy Signals"
            value={buyOpportunities.length}
            icon={TrendingUp}
            color="green"
            trend="up"
            tooltip={TOOLTIPS.buyOpportunities}
          />
          <MetricCard
            label="Sell Signals"
            value={sellSignals.length}
            icon={TrendingDown}
            color="red"
            trend="down"
            tooltip={TOOLTIPS.sellSignals}
          />
        </div>
      )}

      {/* Secondary Metrics */}
      {!loading && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <MetricCard
            label="Stocks Analyzed"
            value={scannerData?.stocks_analyzed || 0}
            icon={BarChart3}
            color="blue"
            tooltip={TOOLTIPS.stocksAnalyzed}
          />
          <MetricCard
            label="Avg Disconnect"
            value={avgDisconnect.toFixed(0)}
            subValue="pts"
            icon={Gauge}
            color="purple"
            tooltip="Average absolute disconnect score across all analyzed stocks"
          />
          <MetricCard
            label="Posts Analyzed"
            value={totalPostsAnalyzed > 0 ? totalPostsAnalyzed.toLocaleString() : '—'}
            icon={MessageSquare}
            tooltip={TOOLTIPS.postsAnalyzed}
          />
          <MetricCard
            label="Sentiment Split"
            value={`${bullishCount}/${neutralCount}/${bearishCount}`}
            subValue="bull/neut/bear"
            icon={Activity}
            tooltip="Distribution of stocks by sentiment: bullish (>20), neutral (-20 to 20), bearish (<-20)"
          />
        </div>
      )}

      {/* Side-by-side Opportunities */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Buy Opportunities */}
        <div className="flex flex-col">
          <div className="flex items-center justify-between mb-3 flex-shrink-0">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-[var(--sentiment-bullish)]/10 flex items-center justify-center">
                <TrendingUp className="w-4 h-4 text-[var(--sentiment-bullish)]" />
              </div>
              <div>
                <h2 className="font-semibold text-[var(--color-text-primary)]">
                  Buy Opportunities
                </h2>
                <p className="text-xs text-[var(--color-text-muted)]">
                  Low sentiment + strong fundamentals
                </p>
              </div>
            </div>
            <span className="text-xs text-[var(--color-text-muted)]">
              {buyOpportunities.length} stocks
            </span>
          </div>

          <div className="space-y-2">
            {loading ? null : buyOpportunities.length > 0 ? (
              buyOpportunities.map((stock, index) => (
                <CompactStockCard
                  key={stock.ticker}
                  stock={stock}
                  rank={index + 1}
                  type="buy"
                  onClick={() => navigate(`/analysis?ticker=${stock.ticker}`)}
                />
              ))
            ) : (
              <div className="text-center py-8 text-[var(--color-text-muted)] bg-[var(--color-bg-secondary)] rounded-lg">
                <TrendingUp className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No buy opportunities found</p>
              </div>
            )}
          </div>
        </div>

        {/* Sell Signals */}
        <div className="flex flex-col">
          <div className="flex items-center justify-between mb-3 flex-shrink-0">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-[var(--sentiment-bearish)]/10 flex items-center justify-center">
                <TrendingDown className="w-4 h-4 text-[var(--sentiment-bearish)]" />
              </div>
              <div>
                <h2 className="font-semibold text-[var(--color-text-primary)]">
                  Sell Signals
                </h2>
                <p className="text-xs text-[var(--color-text-muted)]">
                  High sentiment = stretched
                </p>
              </div>
            </div>
            <span className="text-xs text-[var(--color-text-muted)]">
              {sellSignals.length} stocks
            </span>
          </div>

          <div className="space-y-2">
            {loading ? null : sellSignals.length > 0 ? (
              sellSignals.map((stock, index) => (
                <CompactStockCard
                  key={stock.ticker}
                  stock={stock}
                  rank={index + 1}
                  type="sell"
                  onClick={() => navigate(`/analysis?ticker=${stock.ticker}`)}
                />
              ))
            ) : (
              <div className="text-center py-8 text-[var(--color-text-muted)] bg-[var(--color-bg-secondary)] rounded-lg">
                <TrendingDown className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No sell signals detected</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
